import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Calendar, MessageSquare, FileText, Image, Star, HelpCircle, LogOut,
  Trash2, CheckCircle, Clock, Ban, Sparkles, Send
} from "lucide-react";

const TABS = [
  { id: "appointments", label: "Appointments", icon: Calendar },
  { id: "contact",      label: "Messages",     icon: MessageSquare },
  { id: "blog",         label: "Blog",         icon: FileText },
  { id: "gallery",      label: "Gallery",      icon: Image },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "faqs",         label: "FAQs",         icon: HelpCircle },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("appointments");
  const nav = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem("aarogya_admin_token");
    if (!t) nav("/admin/login");
    else api.get("/auth/me").catch(() => { localStorage.removeItem("aarogya_admin_token"); nav("/admin/login"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("aarogya_admin_token"); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster richColors position="top-center" />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-serif text-lg"
                 style={{ background: "linear-gradient(135deg,#3F2D86,#0A6EBD)" }}>A</div>
            <div>
              <div className="font-serif text-xl text-slate-900">Aarogya Admin</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Clinic Dashboard</div>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-6">
        <aside className="hidden md:block w-56 shrink-0">
          <nav className="space-y-1 sticky top-6">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${tab === t.id ? "bg-[#3F2D86] text-white" : "text-slate-700 hover:bg-slate-100"}`}
                      data-testid={`admin-tab-${t.id}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40">
          <div className="grid grid-cols-6">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                      className={`py-2 flex flex-col items-center gap-1 text-[10px] font-semibold ${tab === t.id ? "text-[#3F2D86]" : "text-slate-500"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 pb-24 md:pb-6">
          {tab === "appointments" && <AppointmentsView />}
          {tab === "contact" && <ContactView />}
          {tab === "blog" && <BlogView />}
          {tab === "gallery" && <GalleryView />}
          {tab === "testimonials" && <TestimonialsView />}
          {tab === "faqs" && <FAQsView />}
        </main>
      </div>
    </div>
  );
}

/* ------------ Panels ------------ */

function AppointmentsView() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/appointments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${id}`); load(); toast.success("Deleted");
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Appointments" desc={`${items.length} total`} />
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items.length === 0 ? <Empty text="No appointments yet." /> : items.map((a) => (
          <div key={a.id} className="p-4 border-b border-slate-100 last:border-0" data-testid={`admin-apt-${a.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-slate-900">{a.name} <span className="text-slate-400 font-normal">· {a.phone}</span></div>
                <div className="text-sm text-slate-600 mt-1">
                  {a.preferred_date} · {a.preferred_time} · {a.mode === "teleconsult" ? "Teleconsult" : "In-clinic"}
                  {a.service ? ` · ${a.service}` : ""}
                </div>
                {a.notes && <div className="mt-1 text-sm text-slate-500">Notes: {a.notes}</div>}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={a.status} />
                <button onClick={() => setStatus(a.id, "confirmed")} className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid={`apt-confirm-${a.id}`}>
                  <CheckCircle className="h-3.5 w-3.5 inline mr-1" /> Confirm
                </button>
                <button onClick={() => setStatus(a.id, "completed")} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100">
                  <Clock className="h-3.5 w-3.5 inline mr-1" /> Done
                </button>
                <button onClick={() => setStatus(a.id, "cancelled")} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                  <Ban className="h-3.5 w-3.5 inline mr-1" /> Cancel
                </button>
                <button onClick={() => remove(a.id)} className="text-xs p-1.5 rounded-full text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactView() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/contact").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.patch(`/contact/${id}/read`); load(); };

  return (
    <div className="space-y-4">
      <PageHeader title="Contact Messages" desc={`${items.length} total`} />
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items.length === 0 ? <Empty text="No messages yet." /> :
          items.map((m) => (
            <div key={m.id} className={`p-4 border-b border-slate-100 last:border-0 ${!m.read ? "bg-blue-50/30" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-900">{m.name} <span className="text-slate-400 font-normal text-sm">· {m.email}</span></div>
                  {m.subject && <div className="text-sm text-slate-700 mt-1">{m.subject}</div>}
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{m.message}</div>
                </div>
                {!m.read && (
                  <button onClick={() => markRead(m.id)} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100" data-testid={`msg-read-${m.id}`}>
                    Mark read
                  </button>
                )}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function BlogView() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAi, setShowAi] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const load = () => api.get("/blog/admin").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const empty = { title: "", excerpt: "", content: "", category: "General", cover_image: "", published: false };

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/blog/${editing.id}`, editing);
      } else {
        await api.post("/blog", editing);
      }
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete post?")) return;
    await api.delete(`/blog/${id}`); load();
  };

  const generateAi = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/blog/ai-draft", { topic: aiTopic });
      setEditing({ ...empty, ...data, published: false });
      setShowAi(false); setAiTopic("");
      toast.success("Draft generated. Please review before publishing.");
    } catch (e) {
      toast.error(e.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : "AI draft failed");
    } finally { setAiLoading(false); }
  };

  if (editing) {
    return (
      <div className="space-y-4">
        <PageHeader title={editing.id ? "Edit post" : "New post"} />
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <Field label="Title"><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className={inp()} data-testid="blog-title-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category"><input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} className={inp()} /></Field>
            <Field label="Cover image URL"><input value={editing.cover_image || ""} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} className={inp()} /></Field>
          </div>
          <Field label="Excerpt"><textarea rows={2} value={editing.excerpt || ""} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} className={inp()} /></Field>
          <Field label="Content (Markdown supported: ###, - lists, paragraphs)">
            <textarea rows={16} value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} className={inp()} data-testid="blog-content-input" />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!editing.published} onChange={e => setEditing({ ...editing, published: e.target.checked })} />
            Publish
          </label>
          <div className="flex gap-2">
            <button onClick={save} className="rounded-full px-5 py-2.5 text-white font-semibold" style={{ background: "#0A6EBD" }} data-testid="blog-save-btn">Save</button>
            <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 text-slate-700 border border-slate-200">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Blog" desc={`${items.length} posts`}
        action={
          <div className="flex gap-2">
            <button onClick={() => setShowAi(true)} className="text-sm rounded-full px-4 py-2 text-white font-semibold" style={{ background: "#3F2D86" }} data-testid="ai-draft-btn">
              <Sparkles className="h-4 w-4 inline mr-1" /> AI Draft
            </button>
            <button onClick={() => setEditing({ ...empty })} className="text-sm rounded-full px-4 py-2 text-white font-semibold" style={{ background: "#0A6EBD" }} data-testid="new-post-btn">
              + New Post
            </button>
          </div>
        }
      />
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items.length === 0 ? <Empty text="No blog posts yet." /> : items.map((p) => (
          <div key={p.id} className="p-4 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-teal-600 font-semibold">{p.category} · {p.published ? "Published" : "Draft"}</div>
              <div className="font-semibold text-slate-900">{p.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">/blog/{p.slug}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">Edit</button>
              <button onClick={() => del(p.id)} className="text-xs p-1.5 rounded-full text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {showAi && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setShowAi(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={e => e.stopPropagation()}>
            <div className="font-serif text-xl text-slate-900">Generate blog draft</div>
            <p className="text-sm text-slate-500 mt-1">Claude will write an evidence-based draft. Doctor must review before publishing.</p>
            <input value={aiTopic} onChange={e => setAiTopic(e.target.value)} placeholder="e.g. Winter respiratory illnesses in children"
                   className={inp() + " mt-4"} data-testid="ai-topic-input" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowAi(false)} className="rounded-full px-4 py-2 text-sm text-slate-700 border border-slate-200">Cancel</button>
              <button onClick={generateAi} disabled={aiLoading} className="rounded-full px-4 py-2 text-sm text-white font-semibold disabled:opacity-60"
                      style={{ background: "#3F2D86" }} data-testid="ai-generate-btn">
                <Send className="h-4 w-4 inline mr-1" /> {aiLoading ? "Generating…" : "Generate draft"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryView() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", image_url: "", category: "clinic" });
  const load = () => api.get("/gallery").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!f.title || !f.image_url) return;
    await api.post("/gallery", f); setF({ title: "", image_url: "", category: "clinic" }); load(); toast.success("Added");
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/gallery/${id}`); load(); };

  return (
    <div className="space-y-4">
      <PageHeader title="Gallery" desc={`${items.length} items`} />
      <form onSubmit={add} className="rounded-2xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-4" data-testid="gallery-add-form">
        <input placeholder="Title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className={inp()} />
        <input placeholder="Image URL" value={f.image_url} onChange={e => setF({ ...f, image_url: e.target.value })} className={inp() + " sm:col-span-2"} />
        <input placeholder="Category" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className={inp()} />
        <button className="sm:col-span-4 rounded-full px-5 py-2.5 text-white font-semibold" style={{ background: "#0A6EBD" }}>Add image</button>
      </form>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((g) => (
          <div key={g.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <img src={g.image_url} alt={g.title} className="w-full aspect-[4/3] object-cover" />
            <div className="p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-teal-600 font-semibold">{g.category}</div>
                <div className="text-sm font-semibold text-slate-800">{g.title}</div>
              </div>
              <button onClick={() => del(g.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-full"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsView() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ patient_name: "", rating: 5, quote: "", location: "", published: true });
  const load = () => api.get("/testimonials/admin").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/testimonials", f);
    setF({ patient_name: "", rating: 5, quote: "", location: "", published: true }); load(); toast.success("Added");
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/testimonials/${id}`); load(); };

  return (
    <div className="space-y-4">
      <PageHeader title="Testimonials" desc={`${items.length} total`} />
      <form onSubmit={add} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <input placeholder="Patient name" value={f.patient_name} onChange={e => setF({ ...f, patient_name: e.target.value })} className={inp()} required />
          <input placeholder="Location" value={f.location} onChange={e => setF({ ...f, location: e.target.value })} className={inp()} />
          <input type="number" min="1" max="5" placeholder="Rating" value={f.rating} onChange={e => setF({ ...f, rating: parseInt(e.target.value) || 5 })} className={inp()} />
        </div>
        <textarea placeholder="Quote" value={f.quote} onChange={e => setF({ ...f, quote: e.target.value })} rows={3} className={inp()} required />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.published} onChange={e => setF({ ...f, published: e.target.checked })} /> Publish
        </label>
        <button className="w-full sm:w-auto rounded-full px-5 py-2.5 text-white font-semibold" style={{ background: "#0A6EBD" }}>Add testimonial</button>
      </form>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items.map((t) => (
          <div key={t.id} className="p-4 border-b border-slate-100 last:border-0 flex items-start justify-between gap-3">
            <div>
              <div className="text-xs text-amber-500">{"★".repeat(t.rating || 5)}{" · "}{t.published ? "Published" : "Hidden"}</div>
              <div className="mt-1 text-slate-700 italic">“{t.quote}”</div>
              <div className="mt-1 text-xs text-slate-500">— {t.patient_name}{t.location ? ` · ${t.location}` : ""}</div>
            </div>
            <button onClick={() => del(t.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-full"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQsView() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ question: "", answer: "", category: "General", order: 100 });
  const load = () => api.get("/faqs").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/faqs", f);
    setF({ question: "", answer: "", category: "General", order: 100 }); load(); toast.success("Added");
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/faqs/${id}`); load(); };
  return (
    <div className="space-y-4">
      <PageHeader title="FAQs" desc={`${items.length} total`} />
      <form onSubmit={add} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <input placeholder="Question" value={f.question} onChange={e => setF({ ...f, question: e.target.value })} className={inp()} required />
        <textarea placeholder="Answer" value={f.answer} onChange={e => setF({ ...f, answer: e.target.value })} rows={3} className={inp()} required />
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Category" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className={inp()} />
          <input type="number" placeholder="Order" value={f.order} onChange={e => setF({ ...f, order: parseInt(e.target.value) || 100 })} className={inp()} />
        </div>
        <button className="rounded-full px-5 py-2.5 text-white font-semibold" style={{ background: "#0A6EBD" }}>Add FAQ</button>
      </form>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items.map((x) => (
          <div key={x.id} className="p-4 border-b border-slate-100 last:border-0 flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-teal-600 font-semibold">{x.category}</div>
              <div className="font-semibold text-slate-900">{x.question}</div>
              <div className="text-sm text-slate-600 mt-1">{x.answer}</div>
            </div>
            <button onClick={() => del(x.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-full"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------ helpers ------------ */
function PageHeader({ title, desc, action }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="font-serif text-2xl text-slate-900">{title}</div>
        {desc && <div className="text-xs text-slate-500">{desc}</div>}
      </div>
      {action}
    </div>
  );
}
function Empty({ text }) { return <div className="p-8 text-center text-slate-500 text-sm">{text}</div>; }
function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700",
    confirmed: "bg-emerald-50 text-emerald-700",
    completed: "bg-blue-50 text-blue-700",
    cancelled: "bg-slate-100 text-slate-600",
  };
  return <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${map[status] || "bg-slate-100"}`}>{status}</span>;
}
const inp = () => "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3F2D86]";
function Field({ label, children }) {
  return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>;
}
